import { useState, useMemo, useCallback } from 'react';
import { buildProjectionGrid, calculateRequiredMonthlySavings } from '@/utils/retirementCalculations';
import type { RetirementPlanningInputs } from '@/types/retirement';
import { PublicHeatmap } from './PublicHeatmap';
import { HeatmapPlaceholder } from './HeatmapPlaceholder';
import { TeaserCta } from './TeaserCta';
import GoalCalculator from './GoalCalculator';

interface CalculatorInputs {
  age: number | null;
  currentSavings: number | null; // dollars, not cents (labeled as "Net Worth")
  monthlyIncome: number | null; // dollars, not cents
  monthlyExpenses: number | null; // dollars, not cents
}

/**
 * Public Retirement Calculator
 * WP-LANDING-5, WP-RETIRE-5: Simplified calculator for unauthenticated visitors
 *
 * Takes inputs (pre-filled) and outputs the full retirement matrix heatmap.
 * Uses same calculation engine as authenticated dashboard.
 * Monthly Savings is derived (Income - Expenses), non-editable.
 */
export function PublicRetirementCalculator() {
  // Pre-filled defaults so heatmap shows immediately
  // WP-RETIRE-5: Income $12,500/mo, Expenses $10,000/mo
  const [inputs, setInputs] = useState<CalculatorInputs>({
    age: 45,
    currentSavings: 1250000, // $1.25M
    monthlyIncome: 12500, // $12,500/mo
    monthlyExpenses: 10000, // $10,000/mo
  });

  // Derived monthly savings
  const monthlySavings = useMemo(() => {
    if (inputs.monthlyIncome === null || inputs.monthlyExpenses === null) return null;
    return inputs.monthlyIncome - inputs.monthlyExpenses;
  }, [inputs.monthlyIncome, inputs.monthlyExpenses]);

  // Build full inputs for calculation engine
  const fullInputs = useMemo(() => {
    if (!isValidInputs(inputs)) return null;
    return buildFullInputs(inputs);
  }, [inputs]);

  // Calculate projection grid when all inputs are valid
  const projectionGrid = useMemo(() => {
    if (!fullInputs) return null;
    const netWorthCents = (inputs.currentSavings ?? 0) * 100;
    return buildProjectionGrid(fullInputs, netWorthCents);
  }, [fullInputs, inputs.currentSavings]);

  // Goal calculator state
  const [goalRetirementAge, setGoalRetirementAge] = useState<number>(65);

  // Goal calculation
  const goalResults = useMemo(() => {
    if (!fullInputs) return null;
    const netWorthCents = (inputs.currentSavings ?? 0) * 100;
    return calculateRequiredMonthlySavings(fullInputs, netWorthCents, goalRetirementAge);
  }, [fullInputs, inputs.currentSavings, goalRetirementAge]);

  // Current monthly savings for the goal calculator "already met" check
  const currentMonthlySavingsDollars = monthlySavings ?? 0;

  // Handler for input changes - defined with useCallback for stable reference
  const handleInputChange = useCallback(
    (field: keyof CalculatorInputs, value: string) => {
      const numValue = value === '' ? null : parseFloat(value);
      setInputs((prev) => ({ ...prev, [field]: numValue }));
    },
    []
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          See your retirement projection
        </h2>
        <p className="text-gray-600">
          Adjust the values below to explore different scenarios.
        </p>
      </div>

      {/* Input Form - WP-RETIRE-5: Income/Expenses/Derived Savings */}
      <div className="grid gap-6 md:grid-cols-2 mb-4">
        {/* Age Input */}
        <div>
          <label
            htmlFor="calculator-age"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Your Age
          </label>
          <input
            id="calculator-age"
            type="number"
            min={18}
            max={90}
            placeholder="e.g., 45"
            value={inputs.age ?? ''}
            onChange={(e) => handleInputChange('age', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
          />
        </div>

        {/* Net Worth Input */}
        <div>
          <label
            htmlFor="calculator-savings"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Net Worth
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              id="calculator-savings"
              type="number"
              min={0}
              placeholder="e.g., 1,500,000"
              value={inputs.currentSavings ?? ''}
              onChange={(e) => handleInputChange('currentSavings', e.target.value)}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
            />
          </div>
        </div>
      </div>

      {/* Income / Expenses / Derived Savings row */}
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        {/* Monthly Income */}
        <div>
          <label
            htmlFor="calculator-income"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Monthly Income
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              id="calculator-income"
              type="number"
              min={0}
              placeholder="e.g., 12,500"
              value={inputs.monthlyIncome ?? ''}
              onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
            />
          </div>
          {inputs.monthlyIncome !== null && (
            <p className="mt-1 text-xs text-gray-400">
              ${((inputs.monthlyIncome ?? 0) * 12).toLocaleString()}/yr
            </p>
          )}
        </div>

        {/* Monthly Expenses */}
        <div>
          <label
            htmlFor="calculator-expenses"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Monthly Expenses
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              id="calculator-expenses"
              type="number"
              min={0}
              placeholder="e.g., 10,000"
              value={inputs.monthlyExpenses ?? ''}
              onChange={(e) => handleInputChange('monthlyExpenses', e.target.value)}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
            />
          </div>
          {inputs.monthlyExpenses !== null && (
            <p className="mt-1 text-xs text-gray-400">
              ${((inputs.monthlyExpenses ?? 0) * 12).toLocaleString()}/yr
            </p>
          )}
        </div>

        {/* Monthly Savings (Derived = Income - Expenses, non-editable) */}
        <div>
          <label
            htmlFor="calculator-derived-savings"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Monthly Savings
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              id="calculator-derived-savings"
              type="text"
              readOnly
              value={monthlySavings !== null ? monthlySavings.toLocaleString() : ''}
              className={`w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-lg cursor-not-allowed ${
                monthlySavings !== null && monthlySavings < 0 ? 'text-red-600' : 'text-gray-700'
              }`}
              tabIndex={-1}
            />
          </div>
          {monthlySavings !== null && (
            <p className={`mt-1 text-xs ${monthlySavings < 0 ? 'text-red-400' : 'text-gray-400'}`}>
              ${(monthlySavings * 12).toLocaleString()}/yr
            </p>
          )}
        </div>
      </div>

      {/* Heatmap or Placeholder */}
      {projectionGrid ? (
        <>
          <PublicHeatmap grid={projectionGrid} />

          {/* Goal Calculator (WP-RETIRE-5 Change 4) */}
          <GoalCalculator
            goalRetirementAge={goalRetirementAge}
            onGoalRetirementAgeChange={setGoalRetirementAge}
            currentAge={inputs.age ?? 45}
            currentMonthlySavings={currentMonthlySavingsDollars}
            goalResults={goalResults}
            showSlider
          />

          <TeaserCta />
        </>
      ) : (
        <HeatmapPlaceholder />
      )}
    </div>
  );
}

/**
 * Validate that all required inputs are present and within bounds
 */
function isValidInputs(inputs: CalculatorInputs): boolean {
  const { age, currentSavings, monthlyIncome, monthlyExpenses } = inputs;

  if (age === null || age < 18 || age > 90) return false;
  if (currentSavings === null || currentSavings < 0) return false;
  if (monthlyIncome === null) return false;
  if (monthlyExpenses === null) return false;

  return true;
}

/**
 * Convert simplified inputs to full API-compatible format
 * WP-RETIRE-5: retirementExpenses = monthlyExpenses * 12
 *              currentSavings = (monthlyIncome - monthlyExpenses) * 12
 */
function buildFullInputs(inputs: CalculatorInputs): RetirementPlanningInputs {
  const age = inputs.age ?? 45;
  const monthlyIncome = inputs.monthlyIncome ?? 0;
  const monthlyExpenses = inputs.monthlyExpenses ?? 0;
  const monthlySavings = monthlyIncome - monthlyExpenses;

  const retirementExpensesCents = Math.round(monthlyExpenses * 12 * 100); // cents
  const annualSavingsCents = Math.round(monthlySavings * 12 * 100); // cents (can be negative)

  return {
    version: 1,
    updatedAt: new Date().toISOString(),
    currentAge: age,
    retirementAge: 65,
    netWorthAdjustmentCents: 0,
    socialSecurityAnnualCents: 0, // Conservative - user can add in full version
    pensionAnnualCents: 0,
    otherRetirementIncomeCents: 0,
    retirementExpensesAnnualCents: retirementExpensesCents,
    currentSavingsAnnualCents: annualSavingsCents,
  };
}

export default PublicRetirementCalculator;
